import React, { Component, Fragment, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import moment from "moment";
import { Driver, MapContext } from "@trackcode/map";
import { isWencoDemoModeEnabled } from "common/wencoDemoMode";
import LayoutContext from "features/app/LayoutContext";
import { getMemberDisplayName } from "features/shipment/WencoCustomersOnMap";
import {
  getDriverLocationLastSignal,
  isDriverLocationActive
} from "state/driver/driverLocationUtils";

function DriverClickTarget({ position, driverId, onClick }) {
  const map = useContext(MapContext);

  useEffect(() => {
    if (!map || !window.google?.maps || !onClick || !position) {
      return undefined;
    }

    const marker = new window.google.maps.Marker({
      position,
      map,
      opacity: 0,
      clickable: true,
      zIndex: 999
    });
    const listener = marker.addListener("click", () => onClick(driverId));

    return () => {
      window.google.maps.event.removeListener(listener);
      marker.setMap(null);
    };
  }, [map, position, driverId, onClick]);

  return null;
}

class ScopeDrivers extends Component {
  static contextType = LayoutContext;

  static propTypes = {
    /** callbacks */
    getBounds: PropTypes.func.isRequired,
    locationsLoaded: PropTypes.bool.isRequired,
    drivers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    locations: PropTypes.shape({}).isRequired
  };

  constructor(props) {
    super(props);
    this.driverLocationsLoaded = false;
    // Re-render to refresh active/inactive styling (TC-599).
    this.refreshDriversInterval = setInterval(this.refreshDrivers, 1000 * 30);
  }

  componentDidMount() {
    const { locationsLoaded, getBounds } = this.props;
    if (!this.driverLocationsLoaded && locationsLoaded) {
      this.driverLocationsLoaded = true;
    }
    const { bounds } = this.getDriversData();
    getBounds(bounds);
  }

  componentWillUnmount() {
    clearInterval(this.refreshDriversInterval);
  }

  componentDidUpdate(prevProps) {
    const { locationsLoaded, getBounds } = this.props;

    if (!this.driverLocationsLoaded && locationsLoaded) {
      this.driverLocationsLoaded = true;
    }

    const locationsChanged = prevProps.locations !== this.props.locations;
    const driversChanged = prevProps.drivers !== this.props.drivers;
    if (locationsChanged || driversChanged) {
      const { bounds } = this.getDriversData();
      getBounds(bounds);
    }
  }

  getDriversData() {
    const { drivers, locations } = this.props;
    const { viewMode, onMapDriverClick } = this.context || {};
    const useWorkbookDriverClick =
      viewMode === "workbook" &&
      !isWencoDemoModeEnabled() &&
      typeof onMapDriverClick === "function";
    const bounds = [];
    const driverIds = Object.keys(locations);

    const driverMarkers = driverIds
      .map(driverId => {
        const driverLocation = locations[driverId];
        const [location] = driverLocation;

        if (!location) return null;

        const lastSignal = getDriverLocationLastSignal(location);

        // only show drivers with a signal today
        if (!lastSignal || !lastSignal.isValid() || !lastSignal.isSame(moment(), "day")) {
          return null;
        }

        // driver information
        const driver = drivers.find(
          ({ id }) => Number(driverId) === Number(id)
        );

        // TC-607 Fix issue if driver not exists in logistic company anymore.
        // This happens when a user switched between logistic companies.
        if (!driver) return null;

        const markerProps = {
          active: isDriverLocationActive(location),
          title: driver ? getMemberDisplayName(driver) : "",
          position: {
            lat: Number(location.latitude),
            lng: Number(location.longitude)
          },
          defaultAnimationDuration: 1500
        };

        if (driver.online === 0) {
          // do not show driver as active on map
          // props.active = false;
        }

        bounds.push(markerProps.position);
        return {
          driverId,
          markerProps,
          useWorkbookDriverClick
        };
      })
      .filter(Boolean);

    return { driverMarkers, bounds };
  }

  /**
   * Update drivers regardless of "location_create" sockets every minute to check active status (TC-599).
   */
  refreshDrivers = () => {
    this.forceUpdate();
  };

  render() {
    const { onMapDriverClick } = this.context || {};
    const { driverMarkers } = this.getDriversData();

    return (
      <Fragment>
        {driverMarkers.map(({ driverId, markerProps, useWorkbookDriverClick }) => (
          <Fragment key={`driver-${driverId}`}>
            <Driver {...markerProps} />
            {useWorkbookDriverClick ? (
              <DriverClickTarget
                position={markerProps.position}
                driverId={Number(driverId)}
                onClick={onMapDriverClick}
              />
            ) : null}
          </Fragment>
        ))}
      </Fragment>
    );
  }
}

const mapStateToProps = ({
  driver: { drivers, locations, locationsLoaded }
}) => ({
  drivers,
  locations,
  locationsLoaded
});

export default connect(mapStateToProps)(ScopeDrivers);
